import { AIChatProvider } from 'components/organisms/AIChat/AIChat.context';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { AIAgentEnum } from 'types/ai.types';
import { logAmplitude } from 'utils/amplitude';
import {
  WEB_AIEMAILREVIEW_EMAILREVIEWCONFIRMED,
  WEB_AIEMAILREVIEW_EMAILREVIEWTRIGGERED,
} from 'utils/amplitude/events/ai.amplitude';
import AIChatMessageEmailActions from '../AIChat/AIChatMessageEmailActions';
import { serializeHtml } from '../TextEditor';
import { adaptChatVariables } from '../TextEditor/serializeHtml';
import type { EditorRef } from '../TextEditor/TextEditor.types';
import AIHelper from './AIHelper.component';
import AIHelperChat from './AIHelperChat';

export type AIHelperEmailWriterProps = {
  className?: string;
  editorRef?: EditorRef;
  variablesData: Record<string, any>;
};

const AIHelperEmailWriter = ({
  editorRef,
  variablesData,
  ...props
}: AIHelperEmailWriterProps): JSX.Element => {
  const { data: user } = useGetCurrentCustomerData();
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);
  const { setMetadata } = useAiMetadata();

  const onVisibilityChange = useCallback(
    (isVisible: boolean) => {
      setVisible(isVisible);

      if (!isVisible || !editorRef?.current) return;

      const newContent = serializeHtml(
        editorRef.current,
        editorRef.current?.children,
      );
      setMetadata({ documentType: 'email', content: newContent }, 'email');
      logAmplitude(WEB_AIEMAILREVIEW_EMAILREVIEWTRIGGERED);
    },
    [setVisible, editorRef],
  );

  const location = useLocation();
  useEffect(() => setMetadata(null, 'email'), [location]);

  const onApprove = useCallback(() => {
    setVisible(false);
    logAmplitude(WEB_AIEMAILREVIEW_EMAILREVIEWCONFIRMED);
  }, [setVisible]);

  // Function to convert HTML to React elements with basic formatting
  const renderFormattedContent = useCallback(
    (htmlContent: string) => {
      // First adapt variables
      const adaptedContent = adaptChatVariables(htmlContent, variablesData);

      // Split by line breaks (preserves paragraphs)
      const paragraphs = adaptedContent
        .split(/<br\s*\/?>/i)
        .filter(Boolean) as string[];

      return paragraphs.map((paragraph, index) => {
        // Create a temporary div to strip HTML tags
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = paragraph;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';

        return (
          <span key={index} className="block mb-2">
            {textContent}
          </span>
        );
      });
    },
    [variablesData],
  );

  return (
    <AIHelper
      tooltip={t('ai_bot.tooltip.email_writer')}
      onVisibilityChange={onVisibilityChange}
      isVisible={visible}
      {...props}
    >
      <AIChatProvider aiAgent={AIAgentEnum.email_writer}>
        <AIHelperChat
          hideRating
          onClose={() => setVisible(false)}
          initialMessage={t('ai_bot.email_writer.initial_message', {
            firstName: user?.firstName,
          })}
          renderMessage={(message) => (
            <p className="inline-block">
              {renderFormattedContent(message.content)}
            </p>
          )}
          lastMessageActions={
            <AIChatMessageEmailActions
              editorRef={editorRef}
              variablesData={variablesData}
              onApprove={onApprove}
            />
          }
        />
      </AIChatProvider>
    </AIHelper>
  );
};

export default AIHelperEmailWriter;
